<template>
  <div class="plagiarism-detected">
    <div v-if="hasFlaggedSentences" class="plagiarism-card">
      <h4 class="card-title heading-xs">
        {{
          $t("essays.plagiarismDetails.flaggedSentences", {
            count: totalFlaggedSentences,
          })
        }}
      </h4>
      <div
        v-for="(essay, idx) in similarEssays"
        :key="'sentence-' + idx"
        class="sentences-list"
      >
        <div
          v-for="(range, rangeIdx) in getSentencesBucket(
            essay.meta.ranges,
            idx
          )"
          :key="'range-' + (idx + 1) * rangeIdx"
          :ref="(el) => setSentenceRef(el, idx, rangeIdx)"
          class="sentence-item"
          :class="{
            'sentence-selected':
              selectedIndex === getSentenceIndex(idx, rangeIdx),
          }"
          @click="$emit('select-sentence', getSentenceIndex(idx, rangeIdx))"
        >
          <!-- Left side - colored dot -->
          <div class="sentence-left">
            <eg-colored-dot-box
              :color="getSimilarityColors(range.similarity).color"
              :background-color="
                getSimilarityColors(range.similarity).background
              "
            />
          </div>

          <!-- Right side - content -->
          <div class="sentence-content">
            <p class="sentence-text paragraph-s">"{{ range.source }}"</p>
            <div class="sentence-footer">
              <span
                class="similarity-text paragraph-s color-text-neutral-secondary"
              >
                {{ $t("essays.similarity") }}
                <span
                  class="similarity-value"
                  :style="{
                    color: getSimilarityColors(range.similarity).color,
                  }"
                >
                  {{ range.similarity }}%
                </span>
              </span>
              <span class="footer-divider" />
              <span class="source-text paragraph-s color-text-neutral-secondary"
                >{{ $t("common.source") }} {{ essay.name }}</span
              >
              <router-link
                target="_blank"
                :to="getEssayUrl(essay)"
                class="source-link"
                @click.native.stop
              >
                <eg-icon icon="share-arrow-sqaure" size="extra-small" />
              </router-link>
            </div>
          </div>
        </div>
        <eg-button
          v-if="shouldShowMoreButton(essay.meta)"
          class="show-more-button"
          type="ordinary"
          size="small"
          @click="toggleShowMore(idx)"
        >
          {{
            showAllSentences[idx]
              ? $t("common.showLess")
              : $t("common.showMore")
          }}
        </eg-button>
      </div>
    </div>

    <!-- Similar Essays Within Account Card -->
    <div v-if="similarEssays?.length" class="plagiarism-card">
      <h4 class="card-title heading-xs">
        {{
          $t("essays.plagiarismDetails.similarEssaysWithin", {
            count: similarEssays.length,
          })
        }}
      </h4>
      <div class="essays-list">
        <div
          v-for="(essay, index) in similarEssays"
          :key="'essay-' + index"
          class="essay-item"
        >
          <div class="essay-info">
            <p class="essay-name paragraph-s medium">{{ essay.name }}</p>
            <div class="essay-meta">
              <span
                class="student-name paragraph-s color-text-neutral-secondary"
                >{{ getStudentName(essay) }}</span
              >
              <span class="meta-divider"></span>
              <span class="essay-date paragraph-s color-text-neutral-secondary">
                {{ formatEssayDate(essay.createdAt) }}
              </span>
            </div>
          </div>
          <div class="essay-actions">
            <similarity-pill :similarity="essay.meta.textualSimilarity" />
            <router-link
              target="_blank"
              :to="getEssayUrl(essay)"
              class="link-button"
            >
              <eg-icon icon="share-arrow-sqaure" size="small" />
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import formatDate from "@/utils/formatDate";
import { getSimilarityColors } from "@/utils/getSimilarityColors";
import EgColoredDotBox from "@/components/global/eg/eg_colored_dot_box.vue";
import SimilarityPill from "@/components/essay/essay/components/similarity_pill.vue";

export default {
  name: "PlagiarismDetected",

  components: {
    EgColoredDotBox,
    SimilarityPill,
  },

  props: {
    essayContent: {
      type: String,
      default: "",
    },
    similarEssays: {
      type: Array,
      default: () => [],
    },
    plagiarismScore: {
      type: Number,
      default: 0,
    },
    selectedIndex: {
      type: Number,
      default: -1,
    },
  },
  data() {
    return {
      showAllSentences: {},
      sentenceIndexMap: new Map(),
      sentenceRefs: new Map(),
    };
  },
  computed: {
    flaggedSentences() {
      return this.similarEssays.filter((essay) => !!essay.meta.ranges.length);
    },
    hasFlaggedSentences() {
      return this.flaggedSentences.length > 0;
    },
    flatSentences() {
      return this.similarEssays.flatMap((essay, essayIdx) =>
        this.getSentencesBucket(essay.meta.ranges, essayIdx).map(
          (range, rangeIdx) => ({
            range: range.range,
            similarity: range.similarity,
            essayIdx,
            rangeIdx,
          })
        )
      );
    },
    totalFlaggedSentences() {
      return this.similarEssays.reduce(
        (total, essay) => total + essay.meta.ranges.length,
        0
      );
    },
  },

  watch: {
    selectedIndex() {
      this.$nextTick(() => this.emitHighlights());
    },
    flatSentences: {
      handler() {
        this.sentenceIndexMap.clear();
        this.flatSentences.forEach((sentence, idx) => {
          const key = `${sentence.essayIdx}_${sentence.rangeIdx}`;
          this.sentenceIndexMap.set(key, idx);
        });

        this.$nextTick(() => this.emitHighlights());
      },
      immediate: true,
    },
  },

  beforeUpdate() {
    this.sentenceRefs.clear();
  },

  mounted() {
    this.$nextTick(() => this.emitHighlights());
  },

  methods: {
    setSentenceRef(el, essayIdx, rangeIdx) {
      if (el) {
        const flatIdx = this.getSentenceIndex(essayIdx, rangeIdx);
        if (flatIdx !== -1) {
          this.sentenceRefs.set(flatIdx, el);
        }
      }
    },
    getSimilarityColors(similarity) {
      return getSimilarityColors(similarity);
    },

    formatEssayDate(date) {
      return formatDate(date, "MMM DD, YYYY");
    },

    getStudentName(essay) {
      const student = essay?.Student;
      if (!student) return "No Student";

      const fullName = `${student.firstName || ""} ${
        student.lastName || ""
      }`.trim();
      return fullName || this.$t("essays.plagiarismDetails.noStudent");
    },

    getEssayUrl(essay) {
      return `/workspace/${essay.folder?.platform}/${essay.folder?.id}/essay/${essay.id}`;
    },

    shouldShowMoreButton(meta) {
      return meta.ranges.length > 2;
    },

    getSentencesBucket(ranges, idx) {
      if (this.showAllSentences[idx]) {
        return ranges;
      }

      return ranges.slice(0, 2);
    },

    toggleShowMore(idx) {
      this.$set(this.showAllSentences, idx, !this.showAllSentences[idx]);
    },

    getSentenceIndex(essayIdx, rangeIdx) {
      const key = `${essayIdx}_${rangeIdx}`;
      return this.sentenceIndexMap.get(key) ?? -1;
    },

    emitHighlights() {
      const sentences = this.flatSentences;

      if (!sentences.length) {
        return this.$emit("updateHighlights", []);
      }

      const sentencesToHighlight =
        this.selectedIndex >= 0 ? [sentences[this.selectedIndex]] : sentences;

      const highlights = sentencesToHighlight.map((sentence, idx) => {
        const actualIndex = this.selectedIndex >= 0 ? this.selectedIndex : idx;
        const colors = this.getSimilarityColors(sentence.similarity);

        return {
          range: sentence.range,
          highlightIndex: actualIndex + 1,
          showHighlightIndex: sentences.length > 1,
          refComponent: this.sentenceRefs.get(actualIndex),
          color: colors.color,
          backgroundColor: colors.background,
        };
      });

      this.$emit("updateHighlights", highlights);
    },
  },
};
</script>

<style lang="scss" scoped>
.plagiarism-detected {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.plagiarism-card {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.5rem;
  border-radius: var(--radius-rd-300, 8px);
  border: 1px solid var(--color-stroke-neutral-default, #e5ebf0);

  .card-title {
    margin: 0;
  }
}

// Sentences list styles
.sentences-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.sentence-item {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  padding: 16px;
  border-radius: var(--radius-rd-400, 12px);
  border: 1px solid var(--color-stroke-neutral-default, #e5ebf0);
  background: var(--color-surface-neutral-background, #fff);
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &:hover {
    background: var(--color-surface-neutral-hover, #f9fafb);
  }

  &.sentence-selected {
    border-color: var(--color-stroke-brand-default, #7c3aed);
    background: var(--color-surface-brand-subtle, #f5f3ff);
  }
}

.sentence-left {
  display: flex;
  align-items: center;
  padding-top: 4px;
}

.sentence-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
  min-width: 0;
}

.sentence-text {
  margin: 0 0 0.25rem 0;
  white-space: pre-wrap;
  word-break: break-word;
}
.sentence-footer {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.similarity-value {
  font-weight: var(--typography-font-weight-fw-700, 700);
}

.footer-divider {
  width: 1px;
  height: 1rem;
  background: #d9d9d9;
}

.source-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 4px;
  border-radius: var(--radius-rd-300, 8px);
  color: var(--color-text-neutral-secondary, #566370);
  text-decoration: none;

  &:hover {
    background: var(--color-surface-neutral-hover, #f5f7f9);
  }
}

.essays-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.essay-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: var(--radius-rd-400, 12px);
  border: 1px solid var(--color-stroke-neutral-default, #e5ebf0);
  background: var(--color-surface-neutral-background, #fff);

  .essay-info {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
  }

  .essay-name {
    margin: 0;
  }

  .essay-meta {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .essay-actions {
    display: flex;
    align-items: center;
    gap: 1rem;
  }
}

.meta-divider {
  width: 1px;
  height: 1rem;
  background: #d9d9d9;
}

.link-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 8px;
  border: none;
  background: transparent;
  border-radius: var(--radius-rd-300, 8px);
  cursor: pointer;
  color: var(--color-text-neutral-on-white, #1c0f30);

  &:hover {
    text-decoration: none;
    background: var(--color-surface-neutral-hover, #f5f7f9);
  }
}
.plagiarism-card .eg-colored-dot-box {
  --color-box-color: #ff3b30;
  background: rgba(255, 59, 48, 0.1);
}
</style>
